import { OutputData } from "@editorjs/editorjs";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CountryPath } from "src/entities/CountryPath";
import { GetArticlesResponse } from "src/entities/GetArticlesResponse";
import { UpdateArticleResponse } from "src/entities/v3/UpdateArticleResponse";
import uploadImageByFile from "src/libs/image/post-image-by-image";
import { PostImageByFileResponseSchemaV2 } from "src/libs/types/image";
import { createHtmlBody } from "src/utils/editorjs/createHtmlBody";
import isPngOrJpeg from "src/utils/isPngOrJpeg";
import { usePut } from "./usePut";

interface Props {
  input: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editorCore: MutableRefObject<any>;
    articleId: string;
    articleState: string; // Current state of the article
    currentThumbnailUrl: string; // Current thumbnail URL
    publisherId: string;
    title: string;
    outputBody: OutputData;
    country: CountryPath;
    acceptedFiles: readonly File[];
    publishedAt: DateTime;
    originalPublishedAt: string; // Original published_at from API (ISO string)
    selectedCategories: string[];
    summary: string;
    companyInfo: string;
    mediaContact: string;
    useThumbnailAsFeaturedImage: boolean;
  };
  functions: {
    handleBackDropOpen: () => void;
    handleClose: () => void;
    handleScheduleClose: () => void;
    handleBackdropClose: () => void;
    handleSave: () => Promise<void>;
    handleComplete: (response: GetArticlesResponse) => void;
  };
}

type ArticleState = "PUBLISHED" | "DRAFT";

export const useUpdateArticle = ({ input, functions }: Props) => {
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  /**
   * Put the article hooks
   */
  const { put } = usePut<UpdateArticleResponse>({
    url: `${process.env.REACT_APP_DOMAIN_V3}/api/v3/articles/${input.articleId}`,
    params: {
      title: input.title,
      body: input.outputBody,
      state: "published",
      summary: input.summary,
      companyInfo: input.companyInfo,
      mediaContact: input.mediaContact,
      businessCategories: [],
      htmlBody: "",
      thumbnailImageUrl: "",
      publishedAt: "",
    },
  });

  /**
   * Update the article
   */
  const updateArticle = async (state: ArticleState) => {
    try {
      if (input.title.length === 0) {
        console.error(t("hooks.no_title"));
        enqueueSnackbar(t("hooks.set_title"), {
          variant: "error",
        });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      if (input.selectedCategories.length === 0) {
        console.error(t("hooks.no_business_categories"));
        enqueueSnackbar(t("hooks.set_business_categories"), {
          variant: "error",
        });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      if (
        input.currentThumbnailUrl.length === 0 &&
        !input.acceptedFiles[0] &&
        state === "PUBLISHED"
      ) {
        enqueueSnackbar(t("hooks.set_thumbnail"), { variant: "error" });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      if (
        input.acceptedFiles.length > 0 &&
        !isPngOrJpeg(input.acceptedFiles[0])
      ) {
        console.error(t("hooks.upload_image_file"));
        enqueueSnackbar(t("hooks.upload_image_file"), { variant: "error" });
        functions.handleClose();
        functions.handleBackdropClose();
        return;
      }

      let data: PostImageByFileResponseSchemaV2 | undefined;
      if (input.acceptedFiles[0]) {
        try {
          data = await uploadImageByFile(input.acceptedFiles[0], () => {
            console.error(t("hooks.image_upload_failed"));
            enqueueSnackbar(t("hooks.image_upload_failed"), {
              variant: "error",
            });
          });

          if (!data?.url) {
            functions.handleClose();
            functions.handleBackdropClose();
            return;
          }
        } catch {
          functions.handleClose();
          functions.handleBackdropClose();
          return;
        }
      }

      const htmlBody = createHtmlBody(input.outputBody, input.summary);

      // Determine the state based on publishedAt
      const articleState =
        state === "PUBLISHED"
          ? input.publishedAt > DateTime.local()
            ? "scheduled"
            : "published"
          : "draft";

      // Use uploaded image or current thumbnail
      const thumbnailUrl = data ? data.url : input.currentThumbnailUrl;

      // Use current time + 10 seconds for immediate publishing, otherwise use user-specified time
      const actualPublishedAt =
        articleState === "published"
          ? DateTime.now().plus({ seconds: 10 })
          : input.publishedAt;

      // Check if publishedAt has changed
      const currentPublishedAt = actualPublishedAt.toUTC().toISO();
      const hasPublishedAtChanged =
        currentPublishedAt !== input.originalPublishedAt;

      // Build params object
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const params: any = {
        title: input.title,
        body: JSON.stringify(input.outputBody),
        htmlBody: htmlBody,
        thumbnailImageUrl: thumbnailUrl,
        state: articleState,
        businessCategories: input.selectedCategories,
        summary: input.summary,
        companyInfo: input.companyInfo,
        mediaContact: input.mediaContact,
        useThumbnailAsFeaturedImage: input.useThumbnailAsFeaturedImage,
      };

      // Only include publishedAt if it has changed AND the original article state is not published
      if (hasPublishedAtChanged && input.articleState !== "published") {
        params.publishedAt = currentPublishedAt;
      }

      put({
        params,
        onSuccess: (response) => {
          if (state === "PUBLISHED") {
            enqueueSnackbar(t("hooks.successfully_published"), {
              variant: "success",
            });
            // If response is null (successful update), navigate to dashboard
            if (!response) {
              navigate("/dashboard");
              return;
            }
            // Only show complete screen if article was draft or payment_failed before
            if (
              input.articleState === "draft" ||
              input.articleState === "payment_failed"
            ) {
              // Convert v3 response to v1 format for compatibility
              // Use input data since v3 response is empty
              const v1Response: GetArticlesResponse = {
                data: {
                  title: input.title,
                  published_at: input.publishedAt.toUTC().toISO(),
                  body: input.outputBody,
                  meta_data: {
                    business_categories: [], // Not available in v3 response
                  },
                  publisherName: "", // Not available in v3 response
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  publisher: {} as any, // Not available in v3 response
                  slug: input.articleId, // Use articleId as slug for v3
                  state: articleState,
                  summary: input.summary,
                  company_info: input.companyInfo,
                  media_contact: input.mediaContact,
                  use_thumbnail_as_featured_image:
                    input.useThumbnailAsFeaturedImage,
                  thumbnail_image_url: thumbnailUrl,
                  created_at: DateTime.now().toUTC().toISO(),
                  search_words: [], // Not available in v3 response
                },
              };
              functions.handleComplete(v1Response);
            } else {
              navigate("/dashboard");
            }
          } else {
            enqueueSnackbar(t("hooks.successfully_saved_as_draft"), {
              variant: "success",
            });
            navigate("/dashboard");
          }
        },
        onError: () => {
          enqueueSnackbar(t("hooks.edit_failed"), { variant: "error" });
        },
        onStart: () => {
          functions.handleBackDropOpen();
          functions.handleSave();
        },
        onEnd: () => {
          functions.handleBackdropClose();
          functions.handleScheduleClose();
          functions.handleClose();
        },
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.message !== "") {
        enqueueSnackbar(e.message, { variant: "error" });
      } else {
        enqueueSnackbar(t("hooks.edit_failed"), { variant: "error" });
      }
    }
  };

  return { updateArticle };
};
